import React, { Component } from 'react';
import './style.scss';
import routeRegistry from '../../../../registries/RouteRegistry';

import { connect } from 'react-redux';
import browserHistory from '../../../../stores/browserHistory';
import { getChangeEmailLabels } from '../../selectors/changeEmailSelector';
import { resolveLocalizedText, stringFormatter } from '../../../../utils';
import { t } from '../../../../locales';
import { contactPermissionPolicy } from '../../types/changeContactService';
import { ENTITY_CODE } from '../../../../constants/constants';

import { Toggle } from '@axa-asia/emma-stylesheet';

interface PolicySelectProps {
  changeEmailLabels: any;
  policyDetails: any;
  policyList: contactPermissionPolicy[];
  onChange: (selectedList: contactPermissionPolicy[]) => void;
}

interface PolicySelectState {
  prevPolicyList?: contactPermissionPolicy[];
  selectedAll: boolean;
  selectedList: contactPermissionPolicy[];
}

class PolicySelect extends Component<PolicySelectProps, PolicySelectState> {
  static getDerivedStateFromProps(
    props: PolicySelectProps,
    state: PolicySelectState,
  ) {
    if (props.policyList !== state.prevPolicyList) {
      const selectedList = props.policyList.map((policy) => ({
        selected: state.selectedAll,
        ...policy,
      }));

      props.onChange(selectedList.filter((policy) => policy.selected));
      return {
        prevPolicyList: props.policyList,
        selectedList,
      };
    }

    // Return null if the state hasn't changed
    return null;
  }

  state = {
    selectedAll: true,
    selectedList: [],
  };

  isUpdatingPNCPolicy =
    this.props.policyDetails.entityCode === ENTITY_CODE.GHK ||
    this.props.policyDetails.entityCode === ENTITY_CODE.G4HK;

  renderOption({ policy, policy: { selected }, onClick }: any) {
    const id = this.getPolicyKey(policy);
    const resolvedOptionPolicyName = this.resolveOptionPolicyName(policy);
    const resolvedOptionPolicyNumbers = this.resolveOptionPolicyNumbers(policy);

    return (
      <div
        key={id}
        className="Policy-Select__option"
        onClick={() => onClick(policy)}
        data-enable-ga
        data-ga-type="button"
        data-ga-text={resolvedOptionPolicyName}
      >
        <div className="Policy-Select__option__row">
          <div className="Policy-Select__option-label">
            <div className="Policy-Select__plan-name">
              {resolvedOptionPolicyName}
            </div>
            <div className="Policy-Select__plan-numbers">
              {resolvedOptionPolicyNumbers}
            </div>
          </div>
          <Toggle id={id} className="col-auto" checked={selected} readOnly />
        </div>
      </div>
    );
  }

  renderGroupOption({
    policies,
    onClick,
  }: {
    policies: contactPermissionPolicy[];
    onClick: () => void;
  }) {
    const id = policies
      .map(
        ({ certificateNumber, policyNumber }) =>
          `${certificateNumber}-${policyNumber}`,
      )
      .join(',');
    const selected = policies.every(({ selected }) => !!selected);

    return (
      <div
        key={id}
        className="Policy-Select__option"
        onClick={() => onClick()}
        data-enable-ga
        data-ga-type="button"
        data-ga-text={t('CUSTOMER_PROFILE_POLICY_GROUP_OPTION_LABEL')}
      >
        <div className="Policy-Select__option__row">
          <div className="Policy-Select__option-label">
            {t('CUSTOMER_PROFILE_POLICY_GROUP_OPTION_LABEL')}
          </div>
          <Toggle id={id} className="col-auto" checked={selected} readOnly />
        </div>
        <div className="Policy-Select__option__row">
          <div>
            {policies.map((policy) => {
              const resolvedOptionPolicyName =
                this.resolveOptionPolicyName(policy);
              const resolvedOptionPolicyNumbers =
                this.resolveOptionPolicyNumbers(policy);

              return (
                <div className="Policy-Select__option-label__li">
                  <div className="Policy-Select__plan-name ">
                    {resolvedOptionPolicyName}
                  </div>
                  <div className="Policy-Select__plan-numbers">
                    {resolvedOptionPolicyNumbers}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="Policy-Select__remarks">
          {t('CUSTOMER_PROFILE_POLICY_GROUP_OPTION_REMARK1')}
          {'\n'}
          {t('CUSTOMER_PROFILE_POLICY_GROUP_OPTION_REMARK2')}
          {/* {t('CUSTOMER_PROFILE_POLICY_GROUP_OPTION_REMARK3')} */}
        </div>
      </div>
    );
  }

  renderGroupPoliciesInText = (policies: contactPermissionPolicy[]) => {
    this.onClickGroupOption(policies, true);
    return (
      <div>
        <div className="Policy-Select__group-label">
          {t('CUSTOMER_PROFILE_POLICY_GROUP_OPTION_LABEL')}
        </div>
        <div>
          {policies.map((policy) => {
            const resolvedOptionPolicyName =
              this.resolveOptionPolicyName(policy);
            const resolvedOptionPolicyNumbers =
              this.resolveOptionPolicyNumbers(policy);
            return (
              <div className="Policy-Select__group-label__li">
                <div className="Policy-Select__group__plan-name ">
                  {resolvedOptionPolicyName}
                </div>
                <div className="Policy-Select__group__plan-numbers">
                  {resolvedOptionPolicyNumbers}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  getPolicyKey = (policy: contactPermissionPolicy) => {
    return `${policy.certificateNumber}-${policy.policyNumber}`;
  };

  resolveOptionPolicyName = (policy: contactPermissionPolicy) => {
    const { changeEmailLabels } = this.props;
    const { policyName, planName } = policy;
    const resolvedPolicyName = resolveLocalizedText(
      planName || policyName || '',
    );
    const resolvedOptionPolicyName = stringFormatter(
      resolveLocalizedText(changeEmailLabels.optionPolicyName),
      {
        policyName: resolvedPolicyName,
      },
    );

    return resolvedOptionPolicyName;
  };

  resolveOptionPolicyNumbers = (policy: contactPermissionPolicy) => {
    const { changeEmailLabels } = this.props;
    const { policyNumber, certificateNumber } = policy;
    const resolvedOptionPolicyNumbers = stringFormatter(
      !certificateNumber
        ? resolveLocalizedText(changeEmailLabels.optionPolicyNumbersWithoutCN)
        : resolveLocalizedText(changeEmailLabels.optionPolicyNumbers),
      {
        policyNumber: policyNumber || '',
        certificateNumber: certificateNumber || '',
      },
    );

    return resolvedOptionPolicyNumbers;
  };

  onClickAll = () => {
    const { onChange } = this.props;
    const { selectedAll, selectedList } = this.state;

    const newSelectedList = selectedList.map(
      (policy: contactPermissionPolicy) => ({
        ...policy,
        selected: this.canSelectPolicy(policy) ? !selectedAll : policy.selected,
      }),
    );
    this.setState(
      {
        selectedAll: newSelectedList.every(
          (policy) => policy.selected || false,
        ),
        selectedList: newSelectedList,
      },
      () =>
        onChange(
          this.state.selectedList.filter(
            (policy: contactPermissionPolicy) => policy.selected,
          ),
        ),
    );
  };

  onClickOption = (policy: contactPermissionPolicy) => {
    if (!this.canSelectPolicy(policy)) return;

    const { onChange } = this.props;
    const { selectedList } = this.state;
    const index = selectedList.findIndex((policyB) => policy === policyB);
    const newList = [...selectedList] as contactPermissionPolicy[];

    newList[index] = { ...policy, selected: !policy.selected };

    const selectedAll = newList.every(
      (policy: contactPermissionPolicy) => policy.selected || false,
    );

    this.setState({ selectedAll, selectedList: newList }, () =>
      onChange(
        this.state.selectedList.filter(
          (policy: contactPermissionPolicy) => policy.selected,
        ),
      ),
    );
  };

  onClickGroupOption = (
    policies: contactPermissionPolicy[],
    forceTrue?: boolean,
  ) => {
    if (policies.length === 0) return;
    if (!this.canSelectPolicy(policies[0])) return;

    const { onChange } = this.props;
    const { selectedList } = this.state;
    const selected = forceTrue || !policies[0].selected;
    const newList: contactPermissionPolicy[] = selectedList.map(
      (policy: contactPermissionPolicy) => {
        if (policies.find((p) => policy === p)) {
          return {
            ...policy,
            selected,
          };
        }
        return policy;
      },
    );

    this.setState(
      {
        selectedAll: newList.every((policy) => policy.selected || false),
        selectedList: newList,
      },
      () =>
        onChange(
          this.state.selectedList.filter(
            (policy: contactPermissionPolicy) => policy.selected,
          ),
        ),
    );
  };

  getPolicyGroups = (policies: contactPermissionPolicy[]) => {
    let ungroupedPolicies: contactPermissionPolicy[] = [];
    let pncPolicies: contactPermissionPolicy[] = [];

    for (const policy of policies) {
      if (
        policy.entityCode === ENTITY_CODE.GHK ||
        policy.entityCode === ENTITY_CODE.G4HK
      ) {
        pncPolicies = [...pncPolicies, policy];
      } else {
        ungroupedPolicies = [...ungroupedPolicies, policy];
      }
    }

    return {
      ungroupedPolicies,
      pncPolicies,
    };
  };

  canSelectPolicy = (policy: contactPermissionPolicy) => {
    if (this.isUpdatingPNCPolicy) {
      return !(
        policy.entityCode === ENTITY_CODE.GHK ||
        policy.entityCode === ENTITY_CODE.G4HK
      );
    }

    return true;
  };

  render() {
    const { changeEmailLabels } = this.props;
    const { selectedAll, selectedList } = this.state;
    const resolvedApplyToAllPolicies = resolveLocalizedText(
      changeEmailLabels.applyToAllPolicies,
    );
    const updatePoliciesLabel = resolveLocalizedText(
      changeEmailLabels.updatePoliciesLabel,
    );
    const { pncPolicies, ungroupedPolicies } =
      this.getPolicyGroups(selectedList);

    const shouldShowPNCOption = !this.isUpdatingPNCPolicy;

    let optionListLength = ungroupedPolicies.length;

    if (shouldShowPNCOption && pncPolicies.length) {
      optionListLength++;
    }

    return (
      <>
        <div className="Policy-Select change-contact-input">
          {!shouldShowPNCOption && pncPolicies.length ? (
            <>
              {this.renderGroupPoliciesInText(pncPolicies)}
              <div className="Policy-Select__remarks">
                {t('CUSTOMER_PROFILE_POLICY_GROUP_OPTION_REMARK1')}
                {'\n'}
                {t('CUSTOMER_PROFILE_POLICY_GROUP_OPTION_REMARK2')}
                {/* {t('CUSTOMER_PROFILE_POLICY_GROUP_OPTION_REMARK3')} */}
              </div>
            </>
          ) : null}
          {optionListLength > 0 ? (
            <div className="Policy-Select__heading">
              <span>{updatePoliciesLabel}</span>
            </div>
          ) : null}
          {optionListLength > 1 ? (
            <div
              key="Policy-Select__option"
              className="Policy-Select__option Policy-Select__option__row"
              onClick={this.onClickAll}
              data-enable-ga
              data-ga-type="button"
              data-ga-text={resolvedApplyToAllPolicies}
            >
              <div className="Policy-Select__option-label">
                <div className="Policy-Select__plan-name">
                  {resolvedApplyToAllPolicies}
                </div>
              </div>
              <Toggle className="col-auto" checked={selectedAll} readOnly />
            </div>
          ) : null}

          {/* order display: EHK -> LHK -> GHK */}
          {ungroupedPolicies.map((policy) =>
            this.renderOption({
              policy,
              onClick: () => this.onClickOption(policy),
            }),
          )}
          {ungroupedPolicies.length && !shouldShowPNCOption ? (
            <div className="Policy-Select__remarks">
              {t('CUSTOMER_PROFILE_POLICY_OPTION_ENQUIRY')}
            </div>
          ) : null}
          {shouldShowPNCOption && pncPolicies.length
            ? this.renderGroupOption({
                policies: pncPolicies,
                onClick: () => this.onClickGroupOption(pncPolicies),
              })
            : null}
        </div>
      </>
    );
  }

  static mapStateToProps(state: any, ownProps: any) {
    const changeEmailLabels = getChangeEmailLabels(state);

    return {
      changeEmailLabels,
    };
  }

  static mapDispatchToProps(dispatch: any) {
    return {};
  }
}

export default connect(
  PolicySelect.mapStateToProps,
  PolicySelect.mapDispatchToProps,
)(PolicySelect);
